import { useCallback } from 'react';
import { useDockviewApi } from './dockview-context.js';
import { useAppStore, useActiveTabId } from './store.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Opens the Methodology panel deep-linked to a given tab — used by the
// "Methodology" / revenue-info buttons across panels (Record vitals, Screener,
// Referrals, Indications). Pass the tab id: 'revenue' | 'signal' |
// 'seller-readiness' | 'index' | 'whitespace' (defaults to 'revenue'). Opened
// floating; the user can dock it. (Data provenance is the separate Sources panel.)

export type MethodologyTab = 'revenue' | 'signal' | 'seller-readiness' | 'index' | 'whitespace';

export function useOpenMethodology(): (tab?: MethodologyTab) => void {
  const activeTabId = useActiveTabId();
  const apiRef = useDockviewApi();
  const addPanel = useAppStore((s) => s.actions.addPanel);

  return useCallback(
    (tab: MethodologyTab = 'revenue') => {
      if (!activeTabId) return;
      const api = apiRef.current;
      if (!api) return;
      const newPanelId = addPanel(activeTabId, { component: 'methodology', title: 'Methodology' });
      const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
      const offset = 24 * cascade;
      api.addPanel<PanelParams>({
        id: newPanelId,
        component: 'methodology' as PanelComponentKey,
        title: 'Methodology',
        params: { tabId: activeTabId, panelId: newPanelId, initialState: { initialTab: tab } },
        floating: {
          position: { left: 140 + offset, top: 90 + offset },
          width: 720,
          height: 560,
        },
      });
    },
    [activeTabId, apiRef, addPanel],
  );
}
